import { Button } from "../../Button";
import { ConfirmationModal } from "../../ConfirmationModal";
import { deleteAttachment, getAttachments } from "../../../api/decision/attachments";
import { PropTypes } from "prop-types";
import { QueryLoader } from "../../Loader";
import { StyledDirectionTableContainer, StyledDirectionTableHeader } from "../../../styles/StyledDecision/StyledContent";
import { StyledModal, StyledPointsModal } from "../../../styles/StyledModal";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AddAttachment from "./AddAttachment";
import AddIcon from "../../../assets/img/icons/add.svg";
import DecisionAttachment from "./DecisionAttachment";
import Modal from "react-modal";
import ModalHeader from "../../ModalHeader/ModalHeader";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

export default function DecisionAttachments({ id, newDecision = false }) {
	const axiosHandler = useAxiosPrivate();

	const [isAddModalOpen, setIsAddModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deletedId, setDeletedId] = useState(null);
	const [tempAttachments, setTempAttachments] = useState([]);

	// For new decisions, don't make API calls - use temporary storage
	const shouldUseAPI = !newDecision && id;
	
	console.log("🔍 DecisionAttachments Debug:", {
		id,
		newDecision,
		shouldUseAPI
	});
	
	const { isLoading, isError, error, data, refetch } = shouldUseAPI 
		? useQuery(["attachmentsTab", id], () => getAttachments(axiosHandler.axiosPrivate, id), {
			refetchOnMount: true, // Always refetch when component mounts
			refetchOnWindowFocus: false,
			staleTime: 0, // Consider data immediately stale
			onSuccess: (data) => {
				console.log("📎 Attachments fetched successfully:", data);
			},
			onError: (error) => {
				console.error("❌ Attachments fetch error:", error);
			}
		})
		: { isLoading: false, isError: false, error: null, data: tempAttachments, refetch: () => {} };

	// Load temporary attachments from sessionStorage for new decisions
	useEffect(() => {
		if (newDecision) {
			const storedData = JSON.parse(sessionStorage.getItem("tempDecisionAttachments") || "[]");
			
			// Check if there's an uploaded file from document processing
			const uploadedFile = sessionStorage.getItem("uploadedFileAttachment");
			if (uploadedFile && storedData.length === 0 && window.tempUploadedFile) {
				try {
					const fileData = JSON.parse(uploadedFile);
					const documentAttachment = {
						id: Date.now(),
						tempId: true,
						name: fileData.name || "Uploaded Document",
						description: "Document uploaded via document processing",
						file: {
							name: fileData.name,
							size: fileData.size,
							type: fileData.type
						},
						tempFile: window.tempUploadedFile
					};
					
					const attachmentsWithDocument = [documentAttachment];
					setTempAttachments(attachmentsWithDocument);
					sessionStorage.setItem("tempDecisionAttachments", JSON.stringify(attachmentsWithDocument));
					
					// Clear the uploaded file references to prevent duplication
					sessionStorage.removeItem("uploadedFileAttachment");
					delete window.tempUploadedFile;
				} catch (error) {
					console.error("Error loading uploaded file attachment:", error);
				}
			} else {
				setTempAttachments(storedData);
			}
		}
	}, [newDecision]);

	useEffect(() => {
		if (isError) {
			console.log("❌ Attachments error:", error);
		}
		if (!isLoading && data) {
			console.log("📎 Attachments data loaded:", data);
		}
	}, [isLoading, isError, error, data]);

	const handleDelete = (attachmentId) => {
		if (newDecision) {
			// For new decisions, remove from temporary storage
			const updatedAttachments = tempAttachments.filter(att => att.id !== attachmentId);
			setTempAttachments(updatedAttachments);
			sessionStorage.setItem("tempDecisionAttachments", JSON.stringify(updatedAttachments));
			setIsDeleteModalOpen(false);
		} else {
			deleteAttachment(
				axiosHandler.axiosPrivate,
				() => {
					setIsDeleteModalOpen(false);
					refetch();
				},
				attachmentId,
				id
			);
		}
	};

	const handleAddAttachment = (newAttachment) => {
		if (newDecision) {
			// For new decisions, add to temporary storage
			const attachmentWithId = {
				...newAttachment,
				id: Date.now(), // Temporary ID
				tempId: true
			};
			const updatedAttachments = [...tempAttachments, attachmentWithId];
			setTempAttachments(updatedAttachments);
			sessionStorage.setItem("tempDecisionAttachments", JSON.stringify(updatedAttachments));
		} else {
			// For existing decisions, use the normal refetch
			refetch();
		}
	};

	return (
		<>
			{isLoading ? (
				<QueryLoader />
			) : (
				<>
					<StyledDirectionTableHeader sizes={[30, 30, 20, 20]}>
						<span>Name</span>
						<span>Description</span>
						<span>Actions</span>
						<span>Actions</span>
					</StyledDirectionTableHeader>
					<StyledDirectionTableContainer>
						{data && data.length > 0 ? (
							data.map((e, i) => (
								<DecisionAttachment key={i} data={e} setDeletedId={setDeletedId} openModal={() => setIsDeleteModalOpen(true)} />
							))
						) : (
							<h6>No attachments added</h6>
						)}
						<Button icon={AddIcon} onClick={() => setIsAddModalOpen(true)}>
							Add attachment
						</Button>
					</StyledDirectionTableContainer>
				</>
			)}

			<Modal
				isOpen={isDeleteModalOpen}
				onRequestClose={() => {
					setDeletedId(null);
					setIsDeleteModalOpen(false);
				}}
				style={StyledModal}
				appElement={document.getElementById("app")}
			>
				<ConfirmationModal
					handleDelete={handleDelete}
					deletedId={deletedId}
					closeModal={() => {
						setDeletedId(null);
						setIsDeleteModalOpen(false);
					}}
				/>
			</Modal>
			<Modal
				isOpen={isAddModalOpen}
				onRequestClose={() => {
					setIsAddModalOpen(false);
				}}
				style={StyledPointsModal}
				appElement={document.getElementById("app")}
			>
				<ModalHeader
					onClick={() => {
						setIsAddModalOpen(false);
					}}
				>
					Add Decision Attachment
				</ModalHeader>
				<AddAttachment 
					id={id} 
					setIsAddModalOpen={setIsAddModalOpen} 
					refetch={refetch}
					newDecision={newDecision}
					onAddAttachment={handleAddAttachment}
				/>
			</Modal>
		</>
	);
}

DecisionAttachments.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	newDecision: PropTypes.bool,
};
